/**
 * Legal compliance register API client (/api/compliance/*).
 *
 * Two resources, and the difference matters:
 *   - an OBLIGATION is "this requirement applies to this site, this often"
 *   - an EVENT is one due period of that obligation
 *
 * Events are generated by the server from the obligation's frequency; the client
 * never creates them. Status is likewise never sent — the server derives it from
 * the due and completed dates, so a page cannot assert "compliant" for a
 * deadline that has passed.
 *
 * Auth is injected by the global axios interceptor in App.js.
 */
import axios from 'axios';

const base = () => `${process.env.REACT_APP_BACKEND_URL}/api/compliance`;

/** Every derived status the register can return, in escalation order. */
export const EVENT_STATUSES = [
  { id: 'overdue', label: 'Overdue', tone: 'rose' },
  { id: 'not_met', label: 'Non-compliance', tone: 'rose' },
  { id: 'due_soon', label: 'Due in 30 days', tone: 'amber' },
  { id: 'evidence_missing', label: 'Evidence missing', tone: 'amber' },
  { id: 'met_late', label: 'Met late', tone: 'sky' },
  { id: 'met', label: 'Met', tone: 'emerald' },
  { id: 'pending', label: 'Not yet due', tone: 'gray' },
];

/** Recurrence options. Must match services/compliance_register.FREQUENCY_MONTHS. */
export const FREQUENCIES = [
  { id: 'one_time', label: 'One time' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'half_yearly', label: 'Half yearly' },
  { id: 'annual', label: 'Annual' },
  { id: 'biennial', label: 'Every 2 years' },
  { id: 'custom_days', label: 'Every N days' },
];

/** Obligations applicable to a site. */
export async function fetchObligations(
  { siteId, includeInactive = false } = {}, { signal } = {},
) {
  const params = {};
  if (siteId) params.site_id = siteId;
  if (includeInactive) params.include_inactive = true;
  const { data } = await axios.get(`${base()}/obligations`, { params, signal });
  return data?.obligations || [];
}

export async function createObligation(body) {
  const { data } = await axios.post(`${base()}/obligations`, body);
  return data;
}

export async function updateObligation(id, body) {
  const { data } = await axios.put(`${base()}/obligations/${id}`, body);
  return data;
}

/** Retire an obligation. Recorded events are kept — they are the audit trail. */
export async function deleteObligation(id, { siteId } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.delete(`${base()}/obligations/${id}`, { params });
  return data;
}

/** The register: { site_id, summary, events } with each event's derived status. */
export async function fetchRegister(
  { siteId, status, limit = 200 } = {}, { signal } = {},
) {
  const params = { limit };
  if (siteId) params.site_id = siteId;
  if (status) params.status = status;
  const { data } = await axios.get(`${base()}/register`, { params, signal });
  return data || { summary: {}, events: [] };
}

/**
 * Record an occurrence as met, or explicitly as not met.
 * body: { completed_date?, evidence_document_id?, non_compliance?,
 *         non_compliance_notes?, capa_id?, notes? }
 */
export async function recordEvent(eventId, body, { siteId } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.put(`${base()}/events/${eventId}`, body, { params });
  return data;
}

/** Top up missing periods. Idempotent; safe to press twice. */
export async function generateEvents({ siteId } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.post(`${base()}/events/generate`, {}, { params });
  return data;
}

/**
 * ---------------------------------------------------------------------------
 * Applicability: what applies to this site, and adopting it
 * ---------------------------------------------------------------------------
 *
 * The catalog PROPOSES; nothing reaches the register until it is adopted. The
 * proposal is inferred from the tenant's industry, the site's jurisdiction and
 * the site's own answers, so every entry comes back with the reasons it applies —
 * a user disagrees with a reason, not with a black box.
 */

/** Severity ordering and presentation for proposed requirements. */
export const SEVERITY_META = {
  critical: { label: 'Critical', cls: 'bg-rose-600 text-white',
              hint: 'Operating without it is an offence or a closure risk' },
  high: { label: 'High', cls: 'bg-amber-500 text-white',
          hint: 'Statutory return or licence with a hard date' },
  medium: { label: 'Medium', cls: 'bg-slate-400 text-white',
            hint: 'Periodic monitoring, records or review' },
};

// REMOVED: fetchCatalogQuestions, fetchSiteProfile, saveSiteProfile.
// The library is no longer gated on a site questionnaire — the whole catalog is
// returned and the site's industry only decides what is highlighted, so there is
// nothing to ask and no profile to store.

/**
 * The WHOLE legal-reference library for the site's jurisdiction, with adoption
 * state and the coverage gap. Nothing is filtered out by industry: each entry
 * carries `industry_match` (typical for this site's industry, sorted first),
 * `industries` (every industry it is typical for) and `reasons`.
 */
export async function fetchApplicableCatalog({ siteId } = {}, { signal } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/catalog`, { params, signal });
  return data || {};
}

/** Adopt the selection. Idempotent per (site, entry) — a re-run adds nothing. */
export async function adoptCatalogEntries(
  { siteId, catalogIds, ownerName, firstDueDate } = {},
) {
  const body = { site_id: siteId, catalog_ids: catalogIds };
  if (ownerName) body.owner_name = ownerName;
  if (firstDueDate) body.first_due_date = firstDueDate;
  const { data } = await axios.post(`${base()}/catalog/adopt`, body);
  return data || {};
}

/**
 * The register as a position: exposure, the next 90 days, ownership gaps and
 * coverage against what applies. Coverage travels with the compliance rate so
 * the two are never read apart.
 */
export async function fetchRegisterInsights({ siteId } = {}, { signal } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/insights`, { params, signal });
  return data || {};
}
